import { useState, useEffect, useContext } from "react";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { getNameInitials } from "../constants/globalFunctions";
import IconComponent from "../icons";
import ProviderApp, { ThemeContext } from "../../ProviderApp";
import { useDispatch, useSelector } from "react-redux";
import UserProfileModal from "../../components/UserProfileModalComponent";
import { fetchUserTagData } from "../../actions/setUserTagData";
import AvatarComponent from "./AvatarComponent";
import _ from "lodash";

function UserNameTagComponent({
  id,
  handleDeleteIconClicked,
  background = "var(--surface-white)",
  showAvatarOnly = false,
  size,
  isCircle,
  isSquare,
  bgColor = "var(--system-colour-2)",
  style,
  maxAvatars,
  userIdsForGroup,
  isExternal = false,
  fullName = "",
  chipBackground = "var(--light-blue-tag)",
  chipBorder = "",
}) {
  return (
    <ProviderApp>
      <UserNameTagComponentWithProvider
        id={id}
        fullName={fullName}
        background={background}
        handleDeleteIconClicked={handleDeleteIconClicked}
        showAvatarOnly={showAvatarOnly}
        size={size}
        isCircle={isCircle}
        isSquare={isSquare}
        bgColor={bgColor}
        isExternal={isExternal}
        style={style}
        maxAvatars={maxAvatars}
        userIdsForGroup={userIdsForGroup}
        chipBackground={chipBackground}
        chipBorder={chipBorder}
      />
    </ProviderApp>
  );
}

function UserNameTagComponentWithProvider({
  id,
  handleDeleteIconClicked,
  showAvatarOnly,
  size,
  isCircle,
  isSquare,
  bgColor = "var(--system-colour-2)",
  isExternal,
  style,
  maxAvatars,
  userIdsForGroup,
  fullName,
  chipBackground,
  chipBorder,
}) {
  const { theme } = useContext(ThemeContext);
  const dispatch = useDispatch();
  const { userTagData } = useSelector((state) => state);

  const [showUserProfileModal, setShowUserProfileModal] = useState(false);

  const handleClick = (e) => {
    e.stopPropagation();
    if (id) {
      setShowUserProfileModal(true);
    }
  };

  const onClose = () => {
    setShowUserProfileModal(false);
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchUserTagData(id));
    }
  }, [id]);

  const getGroupedAvatars = (ids) => {
    const userAvatars = ids.map((id) => (
      <AvatarComponent key={id} fullName={fullName} showAvatarOnly id={id} size={size} isCircle bgColor={bgColor} />
    ));
    return userAvatars;
  };

  let groupedAvatars;

  if (maxAvatars) {
    groupedAvatars = getGroupedAvatars(userIdsForGroup);
  }

  const isLoaded = _.get(userTagData, [id, "isLoaded"]);
  const userData = _.get(userTagData, [id, "data"], {});
  const profilePic = userData?.profilePic;
  const userFullName = userData?.fullName;
  const firstName = userData?.firstName;
  const lastName = userData?.lastName;

  function getInitials() {
    if (id) {
      return getNameInitials({
        firstName: firstName,
        lastName: lastName,
      });
    }
    return getNameInitials({
      firstName: fullName.split(" ")[0] || "",
      lastName: fullName.split(" ")[1] || "",
    });
  }

  return (
    <>
      {(isLoaded || fullName) &&
        (showAvatarOnly ? (
          <AvatarComponent
            bgColor={bgColor}
            size={`${size}`}
            isCircle={isCircle}
            isSquare={isSquare}
            id={id}
            fullName={fullName}
            style={style}
          />
        ) : (
          <Chip
            label={id ? userFullName : (fullName ?? "")}
            deleteIcon={<IconComponent name="cross" />}
            size="medium"
            avatar={
              id && profilePic ? (
                <Avatar src={profilePic} />
              ) : (
                <Avatar>
                  <div className="text-white">
                    {isExternal ? (
                      <div className="bg-orange-400 rounded-full w-8 h-8 flex items-center justify-center">
                        <IconComponent name="userProfileWhite" />
                      </div>
                    ) : (
                      getInitials()
                    )}
                  </div>
                </Avatar>
              )
            }
            onDelete={handleDeleteIconClicked}
            onClick={handleClick}
            sx={{
              background: chipBackground,
              border: chipBorder,
              color: `var(${theme.rootTextColor})`,
              // TODO: tag component label not working inside syncufusion grid component. Will remove it later when we remove syncufsion grids
              lineHeight: "normal",
              ...(isExternal && {
                "&:hover": {
                  backgroundColor: chipBackground,
                },
              }),
            }}
          />
        ))}
      {showUserProfileModal && id && <UserProfileModal userId={id} onClose={onClose} />}

      {maxAvatars && (
        <AvatarComponent
          style={style}
          bgColor={bgColor}
          size={`${size}`}
          isCircle={isCircle}
          isSquare={isSquare}
          maxAvatars={maxAvatars}
          groupedAvatars={groupedAvatars}
          userIdsForGroup={userIdsForGroup}
        />
      )}
    </>
  );
}

export default UserNameTagComponent;
